/**
 * Date formatting utilities for consistent date display across the application
 * Timezone: Asia/Kolkata (IST - India Standard Time)
 * Currency: Indian Rupee (₹)
 * Format: dd/MM/yyyy hh:mm AM/PM
 */

// India Standard Time timezone
const IST_TIMEZONE = 'Asia/Kolkata';

const NAIVE_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/;

/**
 * Convert date to IST timezone
 * Zone-less ISO strings from the backend are UTC — mark them so browsers don't
 * parse them as local time.
 */
const toIST = (dateInput) => {
  if (!dateInput) return null;
  const date = new Date(typeof dateInput === 'string' && NAIVE_ISO_RE.test(dateInput) ? `${dateInput}Z` : dateInput);
  if (isNaN(date.getTime())) return null;
  return date;
};

/** Today's calendar date in IST as yyyy-MM-dd (for date-range presets / inputs). */
export const todayIST = () => formatDateForInput(new Date());

/** Shift a yyyy-MM-dd string by N days (IST calendar). */
export const shiftDateStr = (dateStr, days) => {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Format date to dd/MM/yyyy hh:mm AM/PM in IST
 * @param {string|Date} dateInput - Date string or Date object
 * @param {boolean} includeTime - Whether to include time (default: true)
 * @returns {string} Formatted date string in IST
 */
export const formatDate = (dateInput, includeTime = true) => {
  if (!dateInput) return '-';
  
  const date = toIST(dateInput);
  if (!date) return '-';
  
  // Format using IST timezone
  const options = {
    timeZone: IST_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hour12 = true;
  }
  
  const formatter = new Intl.DateTimeFormat('en-IN', options);
  const parts = formatter.formatToParts(date);
  
  const getPart = (type) => parts.find(p => p.type === type)?.value || '';
  
  const day = getPart('day');
  const month = getPart('month');
  const year = getPart('year');
  
  if (!includeTime) {
    return `${day}/${month}/${year}`;
  }
  
  const hour = getPart('hour');
  const minute = getPart('minute');
  const dayPeriod = getPart('dayPeriod').toUpperCase();
  
  return `${day}/${month}/${year} ${hour}:${minute} ${dayPeriod}`;
};

/**
 * Format date to dd/MM/yyyy only in IST
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string in IST
 */
export const formatDateOnly = (dateInput) => {
  return formatDate(dateInput, false);
};

/**
 * Format date to time only (hh:mm AM/PM) in IST
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted time string in IST
 */
export const formatTimeOnly = (dateInput) => {
  if (!dateInput) return '-';
  
  const date = toIST(dateInput);
  if (!date) return '-';
  
  const formatter = new Intl.DateTimeFormat('en-IN', {
    timeZone: IST_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  const parts = formatter.formatToParts(date);
  const getPart = (type) => parts.find(p => p.type === type)?.value || '';
  
  const hour = getPart('hour');
  const minute = getPart('minute');
  const dayPeriod = getPart('dayPeriod').toUpperCase();
  
  return `${hour}:${minute} ${dayPeriod}`;
};

/**
 * Format date for input fields (yyyy-MM-dd) in IST
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Date in yyyy-MM-dd format
 */
export const formatDateForInput = (dateInput) => {
  if (!dateInput) return '';
  
  const date = toIST(dateInput);
  if (!date) return '';
  
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  return formatter.format(date); // Returns yyyy-MM-dd format
};

/**
 * Format relative time (e.g., "2 hours ago", "Yesterday") in IST
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateInput) => {
  if (!dateInput) return '-';
  
  const date = toIST(dateInput);
  if (!date) return '-';
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return formatDateOnly(dateInput);
};

/**
 * Get current date/time in IST
 * @returns {Date} Current date in IST
 */
export const getCurrentIST = () => {
  return new Date();
};

/**
 * Format currency in Indian Rupees
 * @param {number} amount - Amount to format
 * @param {boolean} showSymbol - Whether to show ₹ symbol (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showSymbol = true) => {
  if (amount === null || amount === undefined) return showSymbol ? '₹0' : '0';
  
  const formatter = new Intl.NumberFormat('en-IN', {
    style: showSymbol ? 'currency' : 'decimal',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  
  return formatter.format(amount);
};

export default {
  formatDate,
  formatDateOnly,
  formatTimeOnly,
  formatDateForInput,
  formatRelativeTime,
  getCurrentIST,
  formatCurrency,
  IST_TIMEZONE
};
